'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatAmount,
  formatDate,
} from '@rr/ui'
import { useCompanyOrders } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function OrdersList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page: String(page),
      limit: String(PAGE_SIZE),
      search: search.trim() || undefined,
    }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyOrders(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by order # or client…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load orders.'
          : 'No orders in this organization.'
      }
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead>Order date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((o) => {
              const rate = o.startingRate
              const currencyCode = o.currency?.code ?? ''
              return (
                <TableRow key={o.id}>
                  <TableCell className="font-medium text-foreground tnum">
                    {o.orderNumber ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">{o.client?.name ?? '—'}</TableCell>
                  <TableCell className="text-sm">{o.manager?.name ?? '—'}</TableCell>
                  <TableCell>
                    <StatusChip status={(o.status ?? 'draft') as any} />
                  </TableCell>
                  <TableCell className="text-right text-sm tnum">
                    {rate != null
                      ? formatAmount(Number(rate), { currency: currencyCode || 'USD' })
                      : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(o.orderDate)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
